import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface PlayerCountry {
  _id: string;
  name: string;
  countryCode: string;
  icon?: string;
}

export interface ApiPlayerListItem {
  _id: string;
  username: string;
  email: string;
  phoneCode?: string | null;
  phone?: string | null;
  countryId?: PlayerCountry | null;
  deviceType?: 'ios' | 'android' | 'web' | null;
  isOnline?: boolean;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  isActive: boolean;
  isSuspended: boolean;
  isMessagingRestricted?: boolean;
  createdAt: string;
  avatar?: string | null;
}

export interface PlayerListFilters {
  countryId?: string;
  status?: 'active' | 'suspended';
  search?: string;
}

export interface PlayerRecentScore {
  gameId: string | null;
  gameName: { en: string; ar: string } | null;
  gameImage: string | null;
  bestScore?: number;
  highScore?: number;
  playCount: number;
  lastPlayedAt: string;
}

export interface PlayerFriend {
  id: string | null;
  username: string | null;
  avatar: string | null;
  email: string | null;
  phoneCode: string | null;
  phone: string | null;
  totalPoints: number;
}

export interface PlayerLastLogin {
  device: string;
  platform: string;
  location: string;
  when: string;
}

export interface PlayerLoginHistoryItem {
  device: string;
  platform: string;
  location: string;
  ipAddress: string;
  deviceToken: string;
  when: string;
}

export interface PlayerAccountHistoryItem {
  _id: string;
  userId: string;
  event: string;
  message?: string;
  adminName: string | null;
  createdAt: string;
}

export interface ApiPlayerProfile {
  _id: string;
  username: string | null;
  email: string;
  phoneCode?: string | null;
  phone?: string | null;
  countryId?: PlayerCountry | null;
  deviceType?: 'ios' | 'android' | 'web' | null;
  isOnline?: boolean;
  gender?: string | null;
  birthdate?: string | null;
  avatar?: string | null;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  isActive: boolean;
  isSuspended?: boolean;
  suspensionReason?: string | null;
  isMessagingRestricted?: boolean;
  messagingRestrictionReason?: string | null;
  isProfileComplete?: boolean;
  totalPoints?: number;
  playStreak?: number;
  createdAt: string;
}

export interface PlayerDetailData {
  profile: ApiPlayerProfile;
  stats: { totalPoints?: number; playStreak?: number; gamesPlayed: number; friendsCount: number };
  recentScores: PlayerRecentScore[];
  friends: PlayerFriend[];
  lastLogin: PlayerLastLogin | null;
  loginHistory: PlayerLoginHistoryItem[];
  accountHistory: PlayerAccountHistoryItem[];
}

export interface PlayerListResponse {
  success: boolean;
  code?: number;
  data: { list: ApiPlayerListItem[]; total: number; page: number; limit: number; totalPages: number };
}

export interface PlayerGetResponse {
  success: boolean;
  code?: number;
  data: PlayerDetailData;
}

export interface CreatePlayerPayload {
  username: string;
  email: string;
  countryId: string;
  phoneCode?: string;
  phone?: string;
  password?: string;
  gender?: string;
  birthdate?: string;
}

export interface PlayerMutationResponse {
  success: boolean;
  code?: number;
  message?: string;
  data?: unknown;
}

export interface SuspendResponse {
  success: boolean;
  code?: number;
  message?: string;
  data?: { isSuspended: boolean; suspensionReason: string };
}

export interface MessagingRestrictionResponse {
  success: boolean;
  code?: number;
  message?: string;
  data?: { isMessagingRestricted: boolean; messagingRestrictionReason?: string };
}

const BASE = `${environment.apiUrl}/api/admin/players`;

@Injectable({ providedIn: 'root' })
export class PlayersService {
  private http = inject(HttpClient);

  list(page = 1, limit = 20, filters: PlayerListFilters = {}): Observable<PlayerListResponse> {
    return this.http.post<PlayerListResponse>(`${BASE}/list`, { page, limit, filters });
  }

  get(id: string): Observable<PlayerGetResponse> {
    return this.http.post<PlayerGetResponse>(`${BASE}/get`, { id });
  }

  create(payload: CreatePlayerPayload, avatar?: File | null): Observable<PlayerMutationResponse> {
    const form = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) form.append(key, String(value));
    });
    if (avatar) form.append('avatar', avatar);
    return this.http.post<PlayerMutationResponse>(`${BASE}/create`, form);
  }

  suspend(id: string, reason: string): Observable<SuspendResponse> {
    return this.http.post<SuspendResponse>(`${BASE}/suspend`, { id, reason });
  }

  unsuspend(id: string): Observable<PlayerMutationResponse> {
    return this.http.post<PlayerMutationResponse>(`${BASE}/unsuspend`, { id });
  }

  sendResetLink(id: string): Observable<PlayerMutationResponse> {
    return this.http.post<PlayerMutationResponse>(`${BASE}/send-reset-link`, { id });
  }

  restrictMessaging(id: string, reason: string): Observable<MessagingRestrictionResponse> {
    return this.http.post<MessagingRestrictionResponse>(`${BASE}/messaging-restriction`, { id, action: 'restrict', reason });
  }

  unrestrictMessaging(id: string): Observable<MessagingRestrictionResponse> {
    return this.http.post<MessagingRestrictionResponse>(`${BASE}/messaging-restriction`, { id, action: 'unrestrict' });
  }
}
